import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { airlineLogo, duffelLogo, gbp } from "@/lib/api";
import { PlaneTakeoff } from "lucide-react";

const ORIGINS = {
  LHR: {
    label: "London", fares: [
      { city: "Dubai", to: "DXB", code: "EK", price: 351 },
      { city: "Istanbul", to: "IST", code: "TK", price: 199 },
      { city: "Jeddah", to: "JED", code: "SV", price: 399 },
      { city: "Delhi", to: "DEL", code: "AI", price: 429 },
      { city: "Islamabad", to: "ISB", code: "EY", price: 489 },
      { city: "Karachi", to: "KHI", code: "PK", price: 489 },
      { city: "Lahore", to: "LHE", code: "QR", price: 499 },
      { city: "Amritsar", to: "ATQ", code: "AI", price: 545 },
      { city: "New York", to: "JFK", code: "VS", price: 345 },
      { city: "Singapore", to: "SIN", code: "SQ", price: 612 },
    ],
  },
  BHX: {
    label: "Birmingham", fares: [
      { city: "Dubai", to: "DXB", code: "EK", price: 389 },
      { city: "Istanbul", to: "IST", code: "TK", price: 239 },
      { city: "Jeddah", to: "JED", code: "SV", price: 439 },
      { city: "Islamabad", to: "ISB", code: "EY", price: 519 },
      { city: "Karachi", to: "KHI", code: "PK", price: 519 },
      { city: "Lahore", to: "LHE", code: "QR", price: 529 },
      { city: "Delhi", to: "DEL", code: "AI", price: 465 },
      { city: "Amritsar", to: "ATQ", code: "AI", price: 575 },
      { city: "Dhaka", to: "DAC", code: "BG", price: 559 },
      { city: "Dubai (2)", to: "DXB", code: "QR", price: 405 },
    ],
  },
};

export const FareStrip = () => {
  const nav = useNavigate();
  const [origin, setOrigin] = useState("LHR");
  const go = (to) => nav(`/flights?origin=${origin}&destination=${to}&cabin=Economy`);
  const { label, fares } = ORIGINS[origin];
  const items = [...fares, ...fares];

  return (
    <section data-testid="fare-strip" className="max-w-7xl mx-auto px-4 sm:px-6 mt-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#00b67a] animate-pulse" />
          <PlaneTakeoff className="w-3.5 h-3.5 text-[#FF6B35]" /> Today's lowest return fares from {label}
        </p>
        <div className="inline-flex items-center rounded-full bg-white/5 ring-1 ring-white/10 p-0.5">
          {Object.entries(ORIGINS).map(([code, o]) => (
            <button
              key={code}
              data-testid={`fare-origin-${code}`}
              onClick={() => setOrigin(code)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${origin === code ? "bg-[#FF6B35] text-white" : "text-slate-300 hover:text-white"}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] py-3 group">
        <div key={origin} className="flex gap-3 w-max animate-marquee group-hover:[animation-play-state:paused]">
          {items.map((f, i) => (
            <button
              key={`${f.to}-${i}`}
              data-testid={`fare-${f.to}-${i}`}
              onClick={() => go(f.to)}
              className="shrink-0 flex items-center gap-3 bg-white rounded-xl pl-2 pr-4 py-2 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <span className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
                <img src={duffelLogo(f.code)} alt={f.code} className="h-5 w-auto object-contain" onError={(e) => { const avs = airlineLogo(f.code); if (e.target.src !== avs) { e.target.src = avs; } else { e.target.style.display = "none"; } }} />
              </span>
              <span className="text-left">
                <span className="block text-[11px] text-slate-400 leading-none">{label} →</span>
                <span className="block text-sm font-semibold text-slate-900 leading-tight">{f.city.replace(" (2)", "")}</span>
              </span>
              <span className="text-[#FF6B35] font-display font-bold text-lg">{gbp(f.price)}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};
